import { get, put, post, patch, deletex, deleteWithBody } from 'utils/request';
import { loadState } from 'utils/localStorage';

/**
 * Create a function to send a request
 * @param  {Function} action - request to be sent (from utils/request)
 * @param  {String}   url - URL to request
 * @param  {String}   token
 * @param  {String}   version - version of API to call
 * @return {Function} a function that accepts 2 parameters
 */
const buildRequest = (action, url, token, version) => {
    let versionHeader = {};
    if (version) {
        versionHeader = {
            headers: {
                'Accept-Version': version,
            },
        };
    }
    /**
     * Inner function - sends an API request
     * @param  {Object} parameters - the URL params or data to be sent (depending on method)
     * @param  {Object} extraArgs - any additional option to pass to axios
     * @return {Promise} axios request promise
     */
    return (parameters, extraArgs = versionHeader) => action(url, parameters, token, extraArgs);
};

/**
 * Create a function to send a request
 * @param  {Function} action - request to be sent (from utils/request)
 * @param  {String}   url - URL to request
 * @param  {String}   token
 * @param  {String}   extraArgs
 * @return {Function} a function that accepts 2 parameters
 */
const buildDownloadRequest =
    (action, url, token, extraArgs) =>
    /**
     * Inner function - sends an API request
     * @param  {Object} parameters - the URL params or data to be sent (depending on method)
     * @param  {Object} extraArgs - any additional option to pass to axios
     * @return {Promise} axios request promise
     */
    (parameters) =>
        action(url, parameters, token, extraArgs);

/**
 * Returns an object with all the stats service's endpoints
 * @param  {Object} params
 * @param  {String} params.propertyId
 * @param  {String} params.token
 * @param  {?*} params.resourceId - if path contains a single resource
 * @param  {?*} params.additionalResourceId - if path contains an additional resource
 * @return {Object} an object with all endpoints (functions)
 */
const statsService = ({ propertyId, token, resourceId, additionalResourceId }) => {
    let baseURL;
    switch (process.env.REACT_APP_TARGET_API) {
        case 'production':
            baseURL = 'https://api.rightrevenue.co.uk';
            break;
        case 'development':
            baseURL = 'https://devapi.rightrevenue.co.uk';
            break;
        case 'test':
            baseURL = 'https://testapi.rightrevenue.co.uk';
            break;
        default:
            baseURL = 'http://localhost:3000';
            break;
    }
    const URL = `${baseURL}/stats/${propertyId}`;
    const NON_STATS_URL = `${baseURL}/${propertyId}`;
    const state = loadState();

    return {
        // Admin
        getAllProperties: buildRequest(get, `${baseURL}/admin/properties`, token),
        getReservationStatuses: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/config/resStatuses`,
            token,
        ),
        addNewProperty: buildRequest(post, `${baseURL}/admin/properties`, token),
        updateProperty: buildRequest(patch, `${baseURL}/admin/properties/${propertyId}`, token),
        onboardProperty: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/onboard`, token),
        updatePropertyFeatures: buildRequest(patch, `${baseURL}/admin/properties/${propertyId}/enabledFeatures`, token),
        getPropertyConfig: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/config`, token),
        getPropertyAEConfig: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/config/ae`, token),
        updateAEConfig: buildRequest(put, `${baseURL}/admin/properties/${propertyId}/config/ae`, token),
        getPropertyCardinalConfig: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/config/cardinal`,
            token,
        ),
        updateCardinalConfig: buildRequest(put, `${baseURL}/admin/properties/${propertyId}/config/cardinal`, token),
        updatePropertyConfig: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/config/updateRecommendationConfig`,
            token,
        ),
        getPropertyVatRates: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/vat`, token),
        createNewPropertyVatRate: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/vat`, token),
        updatePropertyVatRate: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/vat/${resourceId}`,
            token,
        ),
        deletePropertyVatRate: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/vat/${resourceId}`,
            token,
        ),
        getPropertyRoundingRules: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/roundingRules`, token),
        createNewPropertyRoundingRules: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roundingRules`,
            token,
        ),
        updatePropertyRoundingRules: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/roundingRules/${resourceId}`,
            token,
        ),
        deletePropertyRoundingRules: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/roundingRules/${resourceId}`,
            token,
        ),
        getPropertyPercentiles: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/percentiles/room`, token),
        initialisePercentiles: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/percentiles/initialise`,
            token,
        ),
        getAvailablePercentiles: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/percentiles/available`,
            token,
        ),
        createNewPropertyPercentile: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/percentiles/room`,
            token,
        ),
        updatePropertyPercentile: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/percentiles/room/${resourceId}`,
            token,
        ),
        deletePropertyPercentile: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/percentiles/room/${resourceId}`,
            token,
        ),
        getPropertyRateCodes: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/rateCodes`, token),
        syncPropertyRateCodes: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/rateCodes/sync`, token),
        uploadRateCodeData: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/rateCodes/upload`, token),
        getPropertyRateUploadColumnMappings: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/rateCodes/upload/mappings`,
            token,
        ),
        deletePropertyRateCodes: buildRequest(deletex, `${baseURL}/admin/properties/${propertyId}/rateCodes`, token),
        updatePropertyRateCode: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/rateCodes/${resourceId}`,
            token,
        ),
        initialiseDefaulsForRecommendationVersion: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/config/initialise`,
            token,
        ),
        deletePropertyCache: buildRequest(deletex, `${baseURL}/admin/properties/${resourceId}/cache`, token),
        runPropertyForecast: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/forecast`, token),
        runPropertyRecommendations: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/recommendations`,
            token,
        ),
        reRunPropertyRecommendations: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/recommendations/rerun`,
            token,
        ),
        getAllUsers: buildRequest(get, `${baseURL}/admin/users`, token),
        getUser: buildRequest(get, `${baseURL}/admin/users/${resourceId}`, token),
        createUser: buildRequest(post, `${baseURL}/admin/users`, token),
        updateUser: buildRequest(patch, `${baseURL}/admin/users/${resourceId}`, token),
        resetUserPassword: buildRequest(put, `${baseURL}/admin/users/${resourceId}/changePassword`, token),
        deleteUser: buildRequest(deletex, `${baseURL}/admin/users/${resourceId}`, token),
        getAllFeatures: buildRequest(get, `${baseURL}/admin/features`, token),
        createFeature: buildRequest(post, `${baseURL}/admin/features`, token),
        updateFeature: buildRequest(patch, `${baseURL}/admin/features/${resourceId}`, token),
        deleteFeature: buildRequest(deletex, `${baseURL}/admin/features/${resourceId}`, token),
        getAllHurdleRackRates: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/hurdleRackRates`, token),
        upsertHurdleRackRates: buildRequest(put, `${baseURL}/admin/properties/${propertyId}/hurdleRackRates`, token),
        deleteHurdleRackRate: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/hurdleRackRates/${resourceId}`,
            token,
        ),
        bulkCheckHurdleRackRate: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/hurdleRackRates/bulk/check`,
            token,
        ),
        bulkUpdateHurdleRackRate: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/hurdleRackRates/bulk`,
            token,
        ),
        bulkCheckRoomRatePriceLimit: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/bulk/validate`,
            token,
        ),
        bulkUpdateRoomRatePriceLimit: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/bulk/add`,
            token,
        ),
        updateDefaultRoomRatePriceLimit: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/default`,
            token,
        ),
        getAllDefaultRoomRatePriceLimits: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/default`,
            token,
        ),
        getAdvisedRoomRatePriceLimits: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/adviser`,
            token,
        ),
        analyseAdvisedRoomRates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/adviser/analyse`,
            token,
        ),
        saveAdvisedRoomRates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/adviser/save`,
            token,
        ),
        validateAdvisedRoomRates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/adviser/validate`,
            token,
        ),
        getAdvisedRoomRatePriceLimitsExport: buildDownloadRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/adviser/export`,
            token,
            {
                responseType: 'arraybuffer',
            },
        ),
        getAutoAcceptGroups: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2`, token),
        upsertAutoAcceptDate: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/upsert`,
            token,
        ),
        deleteAutoAcceptDate: buildRequest(
            deleteWithBody,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/deleteByDate`,
            token,
        ),
        deleteAutoAcceptGroup: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/${resourceId}`,
            token,
        ),
        bulkCheckAutoAcceptConfig: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/bulk/validate`,
            token,
        ),
        bulkUpdateAutoAcceptConfig: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/bulk/add`,
            token,
        ),
        getAutoAcceptEnabled: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/enabled`,
            token,
        ),
        getAutoAcceptEnabledExists: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/enabled/exists`,
            token,
        ),
        bulkCheckAutoAcceptEnabled: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/enable/validate`,
            token,
        ),
        bulkUpdateAutoAcceptEnabled: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/enable`,
            token,
        ),
        enableGlobalAutoAccept: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/enable/global`,
            token,
        ),
        disableGlobalAutoAccept: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2/disable/global`,
            token,
        ),
        getDerivedRoomGroups: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2`, token),
        upsertDerivedRoomsDate: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2/upsert`,
            token,
        ),
        deleteDerivedRoomsDate: buildRequest(
            deleteWithBody,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2/deleteByDate`,
            token,
        ),
        bulkCheckDerivedRoomConfig: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2/bulk/validate`,
            token,
        ),
        bulkUpdateDerivedRoomConfig: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2/bulk/add`,
            token,
        ),
        saveDerivedRoomGroups: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2`,
            token,
        ),
        deleteDerivedRoomsGroup: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/derivedRoomTypesV2/${resourceId}`,
            token,
        ),
        checkMassUpdateSellingRates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/sellingRates/massupdatecheck`,
            token,
        ),
        postMassUpdateSellingRates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/sellingRates/massupdate`,
            token,
        ),
        getPropertySegmentBuckets: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property`,
            token,
        ),
        getAllMasterSegmentBuckets: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/master`,
            token,
        ),
        // resourceId is the new bucket that the segment is moving to
        addBucketToProperty: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property`,
            token,
        ),
        moveSegmentToNewBucket: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property/${resourceId}/move`,
            token,
        ),
        // resourceId is the bucket to delete
        deleteSegmentBucket: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property/${resourceId}`,
            token,
        ),
        deleteSegmentFromBucket: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property/${resourceId}/remove/${additionalResourceId}`,
            token,
        ),
        updateBucketADRForResData: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property/${resourceId}/updateADR`,
            token,
        ),
        updateBucketADR: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/segmentBuckets/property/${resourceId}`,
            token,
        ),
        getOtaCompsets: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/compsets`, token),
        getOtaConfig: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/config`, token),
        updateOtaConfig: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/ota/config`, token),
        getOtaAlertRules: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/alertRules`, token),
        updateOtaAlertRules: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/ota/alertRules`, token),
        setDefaultOtaCompset: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/ota/compsets/setdefault`,
            token,
        ),
        getPropertyHibernatedDates: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/hibernatedDates`,
            token,
        ),
        bulkInsertHibernatedDates: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/hibernatedDates`,
            token,
        ),
        bulkCheckHibernatedDates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/hibernatedDates/bulk/check`,
            token,
        ),
        bulkUpdateHibernatedDates: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/hibernatedDates/bulk`,
            token,
        ),
        getRoomRatePriceLimits: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits`,
            token,
        ),
        upsertRoomRatePriceLimit: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits`,
            token,
        ),
        checkRoomRatePriceLimit: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/check`,
            token,
        ),
        deleteRoomRatePriceLimit: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/roomRatePriceLimits/${resourceId}`,
            token,
        ),
        getAutoAccept: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2`, token),
        saveAutoAcceptGroupRules: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/autoAcceptRulesV2`,
            token,
        ),
        // resourceId is the bucket to delete
        deleteHibernatedDate: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/hibernatedDates/${resourceId}`,
            token,
        ),
        generateEODStats: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/eodstats/generate`, token),
        repopulateResLookup: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/reservations/repopulateResLookup`,
            token,
        ),
        // Reservation upload
        uploadResData: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/reservations/upload`, token),
        getResUploadColumnMappings: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/reservations/upload/columnMappings`,
            token,
        ),
        // Groups
        getHotelGroupData: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/groups`, token),
        getPropertiesInMyGroup: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/groups/getAllPropertiesInMyGroup`,
            token,
        ),
        updateGroupID: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/groups/update/${resourceId}`,
            token,
        ),
        updateGroupPropertyOrder: buildRequest(
            patch,
            `${baseURL}/admin/properties/${propertyId}/groups/updateGroupPropertyOrder`,
            token,
        ),
        addNewPropertyGroup: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/groups/add`, token),
        getAllOtaChannelAlerts: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts`, token),
        getRestrictions: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/allBuckets`, token),
        actionBucket: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/ota/bucket/action`, token),
        actionMultipleBuckets: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/ota/bucket/actionmultiple`,
            token,
        ),
        bulkCheckOtaChannelAlerts: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts/bulk/check`,
            token,
        ),
        bulkUpdateOtaChannelAlerts: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts/bulk`,
            token,
        ),
        upsertOtaChannelAlerts: buildRequest(put, `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts`, token),
        deleteOtaChannelAlert: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts/${resourceId}`,
            token,
        ),
        acknowledgeOtaChannelAlert: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts/acknowledge/${resourceId}`,
            token,
        ),
        actionOtaChannelAlerts: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/ota/channelAlerts/action`,
            token,
        ),
        getCloseableRates: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/ota/closeableRates`, token),
        // Notes
        getNotes: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/notes`, token),
        updateNote: buildRequest(patch, `${baseURL}/admin/properties/${propertyId}/notes/${resourceId}`, token),
        addNote: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/notes`, token),
        bulkAddNote: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/notes/bulkAdd`, token),
        deleteNote: buildRequest(deletex, `${baseURL}/admin/properties/${propertyId}/notes/${resourceId}`, token),

        // Property Notes
        getAllPropertyNotes: buildRequest(get, `${baseURL}/admin/accountmanagement/notes`, token),
        updatePropertyNote: buildRequest(patch, `${baseURL}/admin/accountmanagement/notes/${resourceId}`, token),
        addPropertyNote: buildRequest(post, `${baseURL}/admin/accountmanagement/notes`, token),
        deletePropertyNote: buildRequest(deletex, `${baseURL}/admin/accountmanagement/notes/${resourceId}`, token),

        // Rate Restrictions
        getRateBuckets: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/rateRestrictions/buckets`, token),
        upsertRateBucketsCheck: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/buckets/check`,
            token,
        ),
        upsertRateBuckets: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/buckets`,
            token,
        ),
        bulkCheckRateRestrictionRules: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/bulk/check`,
            token,
        ),
        bulkUpdateRateRestrictionRules: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/bulk`,
            token,
        ),
        updateDayRateRestrictionRules: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/day`,
            token,
        ),
        getRestrictionsCountByMonth: buildRequest(
            get,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/count/bymonth`,
            token,
        ),
        bulkUpdateRateRestrictionActionsCheck: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/bulk/actions/check`,
            token,
        ),
        bulkUpdateRateRestrictionActions: buildRequest(
            post,
            `${baseURL}/admin/properties/${propertyId}/rateRestrictions/bulk/actions`,
            token,
        ),
        getComponentRooms: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/componentRooms`, token),
        createComponentRoom: buildRequest(post, `${baseURL}/admin/properties/${propertyId}/componentRooms`, token),
        updateComponentRoom: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/componentRooms/${resourceId}`,
            token,
        ),
        deleteComponentRoom: buildRequest(
            deletex,
            `${baseURL}/admin/properties/${propertyId}/componentRooms/${resourceId}`,
            token,
        ),

        getPropertiesInGroup: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/groups/properties`, token),
        getForecastSchedule: buildRequest(get, `${baseURL}/admin/scheduler/${propertyId}/forecast`, token),
        postForecastSchedule: buildRequest(post, `${baseURL}/admin/scheduler/${propertyId}/forecast`, token),
        getRecommendationSchedule: buildRequest(get, `${baseURL}/admin/scheduler/${propertyId}/recommendation`, token),
        postRecommendationSchedule: buildRequest(
            post,
            `${baseURL}/admin/scheduler/${propertyId}/recommendation`,
            token,
        ),
        getAccountManagementRecommendations: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/recommendations/all`,
            token,
        ),
        getAccountManagementRecommendationsOneProperty: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/recommendations/oneproperty`,
            token,
        ),
        getAccountManagementRecommendationsMissing: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/recommendations/missing`,
            token,
        ),
        getAccountManagementForecastMissing: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/forecast/missing`,
            token,
        ),
        getAccountManagementUnconstrainedForecast: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/forecast/unconstrained`,
            token,
        ),
        getAccountManagementLySpitAllProperties: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/lyspit/allproperties`,
            token,
        ),
        getAccountManagementLySpitByProperty: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/lyspit/byproperty`,
            token,
        ),
        getAccountManagementPropertyDetails: buildRequest(
            get,
            `${baseURL}/admin/accountmanagement/propertyDetails`,
            token,
        ),
        getAccountManagementUsers: buildRequest(get, `${baseURL}/admin/accountmanagement/users`, token),
        getAccountManagementVendors: buildRequest(get, `${baseURL}/admin/accountmanagement/vendors`, token),
        getAccountManagementFeatures: buildRequest(get, `${baseURL}/admin/accountmanagement/features`, token),
        getAccountManagementAlerts: buildRequest(get, `${baseURL}/admin/accountmanagement/alerts`, token),
        // New endpoints for cardinal version
        getSellingRates: buildRequest(get, `${baseURL}/${propertyId}/sellingrate-v2`, token),
        updateSellingRates: buildRequest(put, `${baseURL}/${propertyId}/sellingrate-v2`, token),
        checkUpdateSellingRates: buildRequest(put, `${baseURL}/${propertyId}/sellingrate-v2/check`, token),
        updatePriceLimitsFromSellingRateUpdate: buildRequest(
            put,
            `${baseURL}/${propertyId}/sellingrate-v2/priceLimits`,
            token,
        ),
        cancelSellingRateUpdate: buildRequest(post, `${baseURL}/${propertyId}/sellingrate-v2/cancel`, token),
        retrySellingRateUpdate: buildRequest(post, `${baseURL}/${propertyId}/sellingrate-v2/retry`, token),
        getSellingRateReport: buildRequest(get, `${baseURL}/${propertyId}/sellingrate-v2/report`, token),
        getSellingRateEvolution: buildRequest(get, `${baseURL}/${propertyId}/sellingrate-v2/evolution`, token),
        bulkSendSellingRates: buildRequest(post, `${baseURL}/${propertyId}/sellingrate-v2/bulkSend`, token),
        getRoomRecommendationsCountByDay: buildRequest(
            get,
            `${baseURL}/${propertyId}/recommendations/byroom/count/byday`,
            token,
        ),
        getRoomRecommendationsCountByMonth: buildRequest(
            get,
            `${baseURL}/${propertyId}/recommendations/byroom/count/bymonth`,
            token,
        ),
        getRoomRecommendationsAll: buildRequest(get, `${baseURL}/${propertyId}/recommendations/byroom/all`, token),
        getRoomRecommendationsForDashboard: buildRequest(
            get,
            `${baseURL}/${propertyId}/recommendations/byroom/dashboard`,
            token,
        ),
        multiActionRecommendations: buildRequest(
            post,
            `${baseURL}/${propertyId}/recommendations/byroom/multiaction`,
            token,
        ),

        // General stats
        getRoomTypes: buildRequest(get, `${baseURL}/admin/properties/${propertyId}/roomTypes`, token),
        updateRoomType: buildRequest(patch, `${baseURL}/admin/properties/${propertyId}/roomTypes/${resourceId}`, token),
        archiveRoomType: buildRequest(
            put,
            `${baseURL}/admin/properties/${propertyId}/roomTypes/${resourceId}/archive`,
            token,
        ),
        getFinalRoomsAndRevenueByDay: buildRequest(get, `${URL}/roomsrevenue/byday`, token, '2.0.0'),
        getFinalRoomsAndRevenueByMonth: buildRequest(get, `${URL}/roomsrevenue/bymonth`, token, '2.0.0'),
        getPITRoomsAndRevenueByDay: buildRequest(
            get,
            `${URL}/roomsrevenue/pit/byday`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion || '2.0.0',
        ),
        getLySpitRoomsAndRevenueByDay: buildRequest(
            get,
            `${URL}/roomsrevenue/lyspit/byday`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion || '2.0.0',
        ),
        getLySpitRoomsAndRevenueByMonth: buildRequest(
            get,
            `${URL}/roomsrevenue/lyspit/bymonth`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion || '2.0.0',
        ),
        getForecastByDay: buildRequest(get, `${baseURL}/${propertyId}/forecast/byday`, token),
        getForecastSegmentByDay: buildRequest(get, `${baseURL}/${propertyId}/forecast/segment/byday`, token),
        getForecastByMonth: buildRequest(get, `${baseURL}/${propertyId}/forecast/bymonth`, token),
        getForecastTotals: buildRequest(get, `${baseURL}/${propertyId}/forecast/totals`, token),
        getForecastSegmentTotals: buildRequest(get, `${baseURL}/${propertyId}/forecast/segment/totals`, token),
        getBudgetByDay: buildRequest(get, `${URL}/budget/byday`, token),
        getBudgetByMonth: buildRequest(get, `${URL}/budget/bymonth`, token),
        getRawBudget: buildRequest(get, `${URL}/budget/raw`, token),
        updateBudget: buildRequest(patch, `${URL}/budget`, token),
        getBudgetDemandData: buildRequest(get, `${URL}/budget/calculator`, token),
        getBudgetCreatorExport: buildDownloadRequest(post, `${URL}/budgetCreator/export`, token, {
            responseType: 'arraybuffer',
        }),
        getStatisticsByDay: buildRequest(get, `${URL}/statistics/byday`, token),
        getInventoryBlocks: buildRequest(get, `${baseURL}/${propertyId}/inventoryblocks`, token),
        getInventoryBlocksMonthly: buildRequest(get, `${baseURL}/${propertyId}/inventoryblocks/monthly`, token),
        getBookings: buildRequest(get, `${baseURL}/${propertyId}/bookings`, token),
        getTransientCancellationsReport: buildRequest(
            get,
            `${baseURL}/${propertyId}/bookings/cancellations/transientBusiness`,
            token,
        ),
        getFiscalPeriods: buildRequest(get, `${baseURL}/${propertyId}/fiscalPeriods`, token),

        // Widget specifics
        getSegmentationReport: buildRequest(
            get,
            `${URL}/segmentationreport`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getSegmentationReportByBookingPeriod: buildRequest(get, `${URL}/segmentationreport/byBookingPeriod`, token),
        getSegmentationReportByRoomType: buildRequest(get, `${URL}/segmentationreport/roomType`, token),
        getFiscalBreakdownReport: buildRequest(
            get,
            `${URL}/segmentationreport/perFiscalPeriod`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getDailyOTAInsightsWithChange: buildRequest(get, `${URL}/otainsights/daily`, token),
        getOTAInsightsByDay: buildRequest(get, `${URL}/otainsights`, token),
        getOTAInsightsParity: buildRequest(get, `${URL}/otainsights/parity`, token),
        getOTAInsightsByDayExport: buildDownloadRequest(post, `${URL}/otainsights/export`, token, {
            responseType: 'arraybuffer',
        }),
        getPickUp: buildRequest(get, `${baseURL}/${propertyId}/pickup/period`, token),
        getPickUpV2: buildRequest(get, `${baseURL}/${propertyId}/pickup-v2/period`, token),
        getPickUpV3: buildRequest(get, `${baseURL}/${propertyId}/pickup-v3/period`, token),
        getOvernightStats: buildRequest(get, `${URL}/overnightdata`, token),
        getCommissionByPeriod: buildRequest(get, `${URL}/commission`, token),
        getLYSPITCommissionByPeriod: buildRequest(
            get,
            `${URL}/commission/lyspit`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getBusinessChangeByMonth: buildRequest(
            get,
            `${URL}/businesschange/bymonth`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getBusinessChangeByDay: buildRequest(
            get,
            `${URL}/businesschange/byday`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getApaleoProperties: buildRequest(get, `${baseURL}/${propertyId}/vendor/apaleo/connect`, token),
        linkApaleoProperty: buildRequest(post, `${baseURL}/${propertyId}/vendor/link/property`, token),
        getVendorOauth2Info: buildRequest(get, `${baseURL}/${propertyId}/vendor/oauth2info`, token),
        getDashboardV2: buildRequest(get, `${baseURL}/${propertyId}/dashboard`, token),
        deleteDashboardCache: buildRequest(deletex, `${baseURL}/${propertyId}/dashboard/cache`, token),
        getRevenueGain: buildRequest(get, `${baseURL}/${propertyId}/revenuegain`, token),

        // Saves
        getAllSaves: buildRequest(get, `${baseURL}/save`, token),
        getSaveById: buildRequest(get, `${baseURL}/save/${resourceId}`, token),
        createSave: buildRequest(post, `${baseURL}/save`, token),
        updateSave: buildRequest(patch, `${baseURL}/save/${resourceId}`, token),
        deleteSave: buildRequest(deletex, `${baseURL}/save/${resourceId}`, token),
        saveUserConfig: buildRequest(post, `${baseURL}/user/config/${propertyId}`, token),
        saveAllUserConfig: buildRequest(post, `${baseURL}/user/config`, token),
        getAllUserConfig: buildRequest(get, `${baseURL}/user/config`, token),
        getFinanceHubRoomsGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/rooms/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryRoomsGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/rooms/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubRevenueGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/revenue/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryRevenueGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/revenue/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubRoomsUnguaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/rooms/unguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryRoomsUnguaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/rooms/unguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubRevenueUnguaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/revenue/unguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryRevenueUnguaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/revenue/unguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubTotalRoomsGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/rooms/totalguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryTotalRoomsGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/rooms/totalguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubTotalRevenueGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/revenue/totalguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryTotalRevenueGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/revenue/totalguaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubOccupancyGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/occupancy/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryOccupancyGuaranteed: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/occupancy/guaranteed`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubADR: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/adr`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryADR: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/adr`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubRevPar: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/revpar`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryRevPar: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/revpar`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubForecastRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/forecast/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryForecastRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/forecast/revenue`,
            token,
        ),
        getFinanceHubForecastRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/forecast/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryForecastRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/forecast/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubBudgetRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/budget/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryBudgetRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/budget/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubBudgetRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/budget/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryBudgetRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/budget/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSleepers: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/sleepers`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummarySleepers: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/sleepers`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubGroups: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/groups`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryGroups: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/groups`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubGroupsRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/groups/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryGroupsRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/groups/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        deleteFinanceHubDailyStatsCache: buildRequest(deletex, `${NON_STATS_URL}/financehub/cache/dailystats`, token),
        deleteFinanceHubSegmentationCache: buildRequest(
            deletex,
            `${NON_STATS_URL}/financehub/cache/segmentation`,
            token,
        ),
        deleteFinanceHubMonthlyTotalsCache: buildRequest(
            deletex,
            `${NON_STATS_URL}/financehub/cache/monthlytotals`,
            token,
        ),
        getFinanceHubSegment: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/segment/bysegment`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSegmentV2: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/segment/bysegmentV2`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSegmentForecast: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/segment/forecast`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubLYSPITRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/lyspit/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryLYSPITRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/lyspit/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubLYSPITRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/lyspit/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryLYSPITRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/lyspit/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubLYFinalRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/lyfinal/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryLYFinalRevenue: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/lyfinal/revenue`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubLYFinalRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/lyfinal/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryLYFinalRooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/lyfinal/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubOOORooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/ooo/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubSummaryOOORooms: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/summary/ooo/rooms`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        getFinanceHubMonthlyTotals: buildRequest(
            get,
            `${NON_STATS_URL}/financehub/monthly/totals`,
            token,
            state?.session?.authorisedProperties[propertyId]?.config?.pitVersion,
        ),
        // REPORTING
        getGroupPickupData: buildRequest(get, `${NON_STATS_URL}/reporting/group/pickup`, token),
        getGroupPickupExport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/group/pickup/export`, token, {
            responseType: 'arraybuffer',
        }),
        getCustomisedReportExportForSchedule: buildDownloadRequest(
            post,
            `${NON_STATS_URL}/reporting/customised/export/schedule`,
            token,
            { responseType: 'arraybuffer' },
        ),
        getCustomisedReportExport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/customised/export`, token, {
            responseType: 'arraybuffer',
        }),
        getCustomisedReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/customised`, token),
        getRoomRecommendationsReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/recommendations`, token, {
            responseType: 'arraybuffer',
        }),
        getScheduledEmails: buildRequest(get, `${NON_STATS_URL}/reporting/schedule`, token),
        createScheduledEmail: buildRequest(post, `${NON_STATS_URL}/reporting/schedule`, token),
        updateScheduledEmail: buildRequest(patch, `${NON_STATS_URL}/reporting/schedule/${resourceId}`, token),
        deleteScheduledEmail: buildRequest(deletex, `${NON_STATS_URL}/reporting/schedule/${resourceId}`, token),
        getFinanceHubHighLevelReport: buildDownloadRequest(
            post,
            `${NON_STATS_URL}/reporting/financehub/highlevel`,
            token,
            { responseType: 'arraybuffer' },
        ),
        getDailyPickupReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/daily/pickup`, token, {
            responseType: 'arraybuffer',
        }),
        getGroupDailyPickupReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/group/daily/pickup`, token, {
            responseType: 'arraybuffer',
        }),
        getUsersInProperty: buildRequest(get, `${NON_STATS_URL}/user/property`, token),
        getUserPropertyExtraInfo: buildRequest(get, `${NON_STATS_URL}/user/property/extraDetails`, token),
        getUserPropertyConfig: buildRequest(get, `${NON_STATS_URL}/user/property/config`, token),
        getUserAcceptions: buildRequest(get, `${baseURL}/user/config/acceptions`, token),
        getOtaParityReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/ota/parity`, token, {
            responseType: 'arraybuffer',
        }),
        getRevenueGainReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/revenuegain`, token, {
            responseType: 'arraybuffer',
        }),
        getGuestrevuData: buildRequest(get, `${NON_STATS_URL}/guestrevu`, token),
        getFiscalBreakdownReportExport: buildDownloadRequest(
            post,
            `${NON_STATS_URL}/reporting/fiscal/breakdown`,
            token,
            { responseType: 'arraybuffer' },
        ),
        getFiscalBreakdownReportCustomisedExport: buildDownloadRequest(
            post,
            `${NON_STATS_URL}/reporting/fiscal/breakdown/customised`,
            token,
            { responseType: 'arraybuffer' },
        ),
        getUsersReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/users`, token, {
            responseType: 'arraybuffer',
        }),
        getPerformanceEngagementReport: buildDownloadRequest(
            post,
            `${NON_STATS_URL}/reporting/group/performanceEngagement`,
            token,
            {
                responseType: 'arraybuffer',
            },
        ),
        getSalesReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/sales`, token, {
            responseType: 'arraybuffer',
        }),
        getEventsBetweenDates: buildRequest(get, `${NON_STATS_URL}/events`, token),
        getEventCategories: buildRequest(get, `${NON_STATS_URL}/events/categories`, token),
        blacklistEvent: buildRequest(put, `${NON_STATS_URL}/events/blacklist/${resourceId}`, token),
        bulkBlacklistEvents: buildRequest(post, `${NON_STATS_URL}/events/blacklist/bulk`, token),
        updateEventCategoryRanking: buildRequest(put, `${NON_STATS_URL}/events/categories/`, token),
        getWeather: buildRequest(get, `${baseURL}/${propertyId}/weather`, token),
        getDemandCalendarDashboard: buildRequest(get, `${baseURL}/${propertyId}/demandCalendar/dashboard`, token),
        getRevFlow: buildRequest(get, `${NON_STATS_URL}/revflow`, token),
        getRevFlowReport: buildDownloadRequest(post, `${NON_STATS_URL}/reporting/revFlow`, token, {
            responseType: 'arraybuffer',
        }),
        getNotifications: buildRequest(get, `${NON_STATS_URL}/notifications`, token),
        getUsersToSendNotification: buildRequest(get, `${NON_STATS_URL}/notifications/users`, token),
        createNotification: buildRequest(post, `${baseURL}/admin/notifications`, token),
        acknowledgeNotification: buildRequest(put, `${NON_STATS_URL}/notifications/${resourceId}/acknowledge`, token),
        dismissNotification: buildRequest(put, `${NON_STATS_URL}/notifications/${resourceId}/dismiss`, token),
    };
};

/**
 * Returns an object with all the auth service's endpoints
 * @param  {Object} params
 * @param  {String} params.token
 * @return {Object} an object with all endpoints (functions)
 */
const authService = ({ token } = {}) => {
    let baseURL;
    switch (process.env.REACT_APP_TARGET_API) {
        case 'production':
            baseURL = 'https://api.rightrevenue.co.uk';
            break;
        case 'development':
            baseURL = 'https://devapi.rightrevenue.co.uk';
            break;
        case 'test':
            baseURL = 'https://testapi.rightrevenue.co.uk';
            break;
        default:
            baseURL = 'http://localhost:3000';
            break;
    }
    return {
        authenticate: buildRequest(post, `${baseURL}/authentication`),
        setUserPreferences: buildRequest(put, `${baseURL}/user/preferences`, token),
        getUserProperties: buildRequest(get, `${baseURL}/user/config/property/access`, token),
        requestReset: buildRequest(post, `${baseURL}/authentication/forgotPassword`),
        resetPassword: buildRequest(post, `${baseURL}/authentication/passwordReset`),
    };
};

export { buildRequest, statsService, authService };
